// src/pages/Dashboard.js
import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import StatCard from '../components/StatCard';
import StatusBadge from '../components/StatusBadge';
import Pagination from '../components/Pagination';
import Table from '../components/Table';
import {
  ClipboardListIcon, ClockIcon, CheckCircleIcon, XCircleIcon, SearchIcon,
  TrendingUpIcon, PieChartIcon, BuildingIcon, AlertTriangleIcon,
} from '../components/Icon';
import { api } from '../utils/api';

const PIE_COLORS = ['#b8862e', '#d4a84b', '#7a5a1e', '#e8c070', '#a07025'];
const BHK_COLORS = { '2 BHK': '#27ae60', '3 BHK': '#b8862e', '4 BHK': '#2980b9' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Code names from submissions vs the projects master can differ in case or
// stray whitespace. Normalise before comparing so a submission is never hidden
// from the dashboard just because of casing or trailing spaces.
const norm = (s) => String(s || '').trim().toLowerCase();
const ucfirst = (s) => { s = String(s || '').trim(); return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Other'; };
const monthLabel = (ym) => { const [y, m] = ym.split('-'); return `${MONTHS[(+m) - 1] || ''} ${y}`; };

export default function Dashboard({ onNavigate }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedCodeName, setSelectedCodeName] = useState('All');
  const [activeProjects, setActiveProjects] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  // Active projects power the dropdown + the "belongs to an active project" filter.
  useEffect(() => {
    api.projects().then(res => {
      if (res && res.projects) {
        setActiveProjects(res.projects.filter(p => p.is_active === '1' || p.is_active === 1 || p.is_active === true));
      }
    }).catch(() => {});
  }, []);

  // All submissions — every dashboard number (cards, charts, table) is derived
  // from this single source, so they can never disagree with each other.
  useEffect(() => {
    setLoading(true);
    api.list({ limit: 9999, page: 1 })
      .then(res => { setAllRows((res && res.data) || []); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  // Submissions that belong to an ACTIVE project only. Inactive (or unknown)
  // projects are excluded everywhere on the dashboard.
  const activeRows = useMemo(() => {
    const activeNames = new Set(activeProjects.map(p => norm(p.code_name)));
    return allRows.filter(r => r.code_name && activeNames.has(norm(r.code_name)));
  }, [allRows, activeProjects]);

  // Rows for the currently selected project (or all active projects).
  const filteredRows = useMemo(() => (
    selectedCodeName === 'All'
      ? activeRows
      : activeRows.filter(r => norm(r.code_name) === norm(selectedCodeName))
  ), [activeRows, selectedCodeName]);

  // Every stat card + chart, computed client-side from the filtered rows.
  const stats = useMemo(() => {
    const byStatus = {}, monthMap = {}, sourceMap = {}, bhkMap = {};
    filteredRows.forEach(r => {
      const st = r.status || 'Pending';
      byStatus[st] = (byStatus[st] || 0) + 1;
      if (r.created_at) { const ym = String(r.created_at).slice(0, 7); monthMap[ym] = (monthMap[ym] || 0) + 1; }
      const src = ucfirst(r.source_type || r.source_name || 'Other');
      sourceMap[src] = (sourceMap[src] || 0) + 1;
      const bhk = r.bhk_config || 'N/A';
      bhkMap[bhk] = (bhkMap[bhk] || 0) + 1;
    });
    return {
      total: filteredRows.length,
      byStatus,
      monthly: Object.keys(monthMap).sort().slice(-6).map(ym => ({ month: monthLabel(ym), count: monthMap[ym] })),
      bySource: Object.entries(sourceMap).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value),
      byBhk: Object.entries(bhkMap).map(([name, value]) => ({ name, value })),
    };
  }, [filteredRows]);

  // Distribution of submissions across active projects (code names).
  const codeNameDist = useMemo(() => {
    const counts = {};
    activeRows.forEach(r => { counts[r.code_name] = (counts[r.code_name] || 0) + 1; });
    return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [activeRows]);

  // Reset to first page whenever the table's data set changes
  useEffect(() => { setPage(1); }, [selectedCodeName, pageSize]);

  if (loading) return <Loader />;
  if (error) return <Error />;

  const byStatus = stats.byStatus;
  const total = stats.total;

  // BHK distribution is only meaningful for apartments; for everything else
  // (incl. "All") we show the project-wise (code name) distribution instead.
  const selectedProject = activeProjects.find(p => norm(p.code_name) === norm(selectedCodeName));
  const isApartment = selectedProject?.project_type === 'Apartment';

  const pagedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  const recentColumns = [
    { key: 'ref_number', header: 'Ref No', nowrap: true,
      tdStyle: { color: '#b8862e', fontWeight: 700, fontFamily: 'Georgia,serif' }, render: (r) => r.ref_number },
    { key: 'name1', header: 'Applicant', render: (r) => r.name1 },
    { key: 'bhk_config', header: 'BHK', align: 'center', render: (r) => r.bhk_config || '—' },
    { key: 'price_range', header: 'Price Range', render: (r) => r.price_range },
    { key: 'source', header: 'Source', render: (r) => r.source_name || r.source_type || <span className="tbl-dash">—</span> },
    { key: 'status', header: 'Status', align: 'center', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'created_at', header: 'Submitted On', nowrap: true,
      tdStyle: { color: '#888', fontSize: 12 }, render: (r) => r.created_at?.split(' ')[0] },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <div className="page-sub">Welcome back · Guru Punvaanii Admin</div>
        </div>
        <div className="dash-header-controls">
          <select
            value={selectedCodeName}
            onChange={e => setSelectedCodeName(e.target.value)}
            className="dash-dropdown"
          >
            <option value="All">Select Code Names</option>
            {activeProjects.map(p => (
              <option key={p.id} value={p.code_name}>{p.code_name}</option>
            ))}
          </select>
          <div className="dash-date-chip">{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>
      </div>

      {/* STAT CARDS */}
      <div className="dash-stats-grid">
        <StatCard label="Total Applications" value={total}                  icon={<ClipboardListIcon size={20} />} color="#b8862e" sub="All time EOI submissions" />
        <StatCard label="Pending"            value={byStatus.Pending  || 0} icon={<ClockIcon size={20} />}         color="#f39c12" sub="Awaiting review" />
        <StatCard label="Approved"           value={byStatus.Approved || 0} icon={<CheckCircleIcon size={20} />}   color="#27ae60" sub="Successfully approved" />
        <StatCard label="Rejected"           value={byStatus.Rejected || 0} icon={<XCircleIcon size={20} />}       color="#c0392b" sub="Not processed" />
        <StatCard label="Reviewed"           value={byStatus.Reviewed || 0} icon={<SearchIcon size={20} />}        color="#2980b9" sub="Under consideration" />
      </div>

      {/* CHARTS ROW */}
      <div className="dash-charts-row">
        {/* Monthly Bar Chart */}
        <div className="dash-chart-card">
          <div className="dash-chart-title"><TrendingUpIcon size={15} /> <span>Monthly Submissions</span></div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats.monthly} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0e8d4" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#888' }} />
              <YAxis tick={{ fontSize: 11, fill: '#888' }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 6, border: '1px solid #e0d0a8', fontSize: 12 }} />
              <Bar dataKey="count" fill="#b8862e" radius={[4, 4, 0, 0]} name="Submissions" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Source Pie */}
        <div className="dash-chart-card">
          <div className="dash-chart-title"><PieChartIcon size={15} /> <span>Source Breakdown</span></div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={stats.bySource} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                {stats.bySource.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 6, border: '1px solid #e0d0a8', fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* BHK Pie (apartments only) — otherwise project-wise distribution */}
        {isApartment ? (
          <div className="dash-chart-card">
            <div className="dash-chart-title"><BuildingIcon size={15} /> <span>BHK Distribution</span></div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={stats.byBhk} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4}>
                  {stats.byBhk.map((entry, i) => <Cell key={i} fill={BHK_COLORS[entry.name] || PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="dash-chart-card">
            <div className="dash-chart-title"><BuildingIcon size={15} /> <span>Project Distribution</span></div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={codeNameDist} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4}>
                  {codeNameDist.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* RECENT TABLE */}
      <div className="dash-recent-card">
        <div className="dash-recent-header">
          <div className="dash-chart-title"><ClockIcon size={15} /> <span>Recent Submissions</span></div>
          <button className="dash-view-all" onClick={() => onNavigate('submissions')}>View All →</button>
        </div>
        <Table
          columns={recentColumns}
          data={pagedRows}
          rowKey={(r) => r.id}
          minWidth={760}
          emptyMessage="No submissions yet"
        />
        <Pagination
          page={page}
          pageSize={pageSize}
          total={filteredRows.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </div>
  );
}

const Loader = () => (
  <div className="dash-loader">
    <div className="spinner spinner--lg" />
  </div>
);

const Error = () => (
  <div className="dash-error">
    <div><AlertTriangleIcon size={42} /></div>
    <div className="dash-error-title">Cannot connect to API</div>
    <div className="dash-error-sub">Make sure XAMPP is running and the PHP API is placed correctly.</div>
  </div>
);
